import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { createSearchEventsTicket } from "../store/leadsSlice";

const getTicketFromResponse = (response) => {
    if (typeof response === "string") return response;

    return (
        response?.ticket ||
        response?.events_ticket ||
        response?.access_ticket ||
        null
    );
};

const safeParseEventData = (event) => {
    if (!event?.data) return {};

    try {
        return JSON.parse(event.data);
    } catch {
        return null;
    }
};

const buildSearchEventsUrl = ({ searchId, eventsUrl, ticket }) => {
    const baseEventsUrl =
        eventsUrl || `/search/${encodeURIComponent(searchId)}/events`;

    if (/^https?:\/\//i.test(baseEventsUrl)) {
        const url = new URL(baseEventsUrl);
        url.searchParams.set("ticket", ticket);
        return url.toString();
    }

    if (process.env.REACT_APP_API_BASE_URL) {
        const url = new URL(baseEventsUrl, process.env.REACT_APP_API_BASE_URL);
        url.searchParams.set("ticket", ticket);
        return url.toString();
    }

    const separator = baseEventsUrl.includes("?") ? "&" : "?";
    return `${baseEventsUrl}${separator}ticket=${encodeURIComponent(ticket)}`;
};

const useSearchEvents = ({
    searchId,
    enabled = true,
    eventsUrl = null,
    onCompleted,
    onFailed,
    onSnapshot,
} = {}) => {
    const dispatch = useDispatch();
    const eventSourceRef = useRef(null);
    const callbacksRef = useRef({ onCompleted, onFailed, onSnapshot });

    const [status, setStatus] = useState("idle");
    const [leadCount, setLeadCount] = useState(null);
    const [error, setError] = useState(null);
    const [errorSearchId, setErrorSearchId] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [lastEvent, setLastEvent] = useState(null);

    useEffect(() => {
        callbacksRef.current = { onCompleted, onFailed, onSnapshot };
    }, [onCompleted, onFailed, onSnapshot]);

    const closeEventSource = useCallback((updateConnectionState = true) => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        if (updateConnectionState) {
            setIsConnected(false);
            setIsConnecting(false);
        }
    }, []);

    const closeConnection = useCallback(() => {
        closeEventSource(true);
    }, [closeEventSource]);

    useEffect(() => {
        let cancelled = false;

        closeEventSource(false);

        if (!enabled || !searchId) {
            setIsConnected(false);
            setIsConnecting(false);
            setError(null);
            setErrorSearchId(null);
            return () => {
                cancelled = true;
                closeEventSource(false);
            };
        }

        const handleInvalidEvent = (eventType, shouldClose = false) => {
            if (cancelled) return;

            setError("Invalid search event received.");
            setErrorSearchId(searchId);
            setLastEvent({ type: eventType, data: null });

            if (shouldClose) {
                closeEventSource(true);
            }
        };

        const openSearchEvents = async () => {
            if (typeof EventSource === "undefined") {
                setError("Search events are not supported in this browser.");
                setErrorSearchId(searchId);
                return;
            }

            setError(null);
            setErrorSearchId(null);
            setIsConnecting(true);
            setIsConnected(false);

            try {
                const ticketResponse = await dispatch(
                    createSearchEventsTicket(searchId),
                ).unwrap();

                if (cancelled) return;

                const ticket = getTicketFromResponse(ticketResponse);

                if (!ticket) {
                    throw new Error("Search events ticket was not returned.");
                }

                const source = new EventSource(
                    buildSearchEventsUrl({ searchId, eventsUrl, ticket }),
                );

                eventSourceRef.current = source;

                source.onopen = () => {
                    if (cancelled) return;

                    setIsConnecting(false);
                    setIsConnected(true);
                };

                source.addEventListener("search.snapshot", (event) => {
                    if (cancelled) return;

                    const data = safeParseEventData(event);

                    if (!data) {
                        handleInvalidEvent("search.snapshot");
                        return;
                    }

                    setLastEvent({ type: "search.snapshot", data });

                    if (data.status) {
                        setStatus(data.status);
                    }

                    if (data.lead_count !== undefined) {
                        setLeadCount(data.lead_count);
                    }

                    callbacksRef.current.onSnapshot?.(data);
                });

                source.addEventListener("search.completed", (event) => {
                    if (cancelled) return;

                    const data = safeParseEventData(event);

                    if (!data) {
                        handleInvalidEvent("search.completed", true);
                        return;
                    }

                    setStatus("completed");
                    setLastEvent({ type: "search.completed", data });

                    if (data.lead_count !== undefined) {
                        setLeadCount(data.lead_count);
                    }

                    callbacksRef.current.onCompleted?.(data);
                    closeEventSource(true);
                });

                source.addEventListener("search.failed", (event) => {
                    if (cancelled) return;

                    const data = safeParseEventData(event);

                    if (!data) {
                        handleInvalidEvent("search.failed", true);
                        return;
                    }

                    setStatus("failed");
                    setLastEvent({ type: "search.failed", data });
                    callbacksRef.current.onFailed?.(data);
                    closeEventSource(true);
                });

                source.addEventListener("heartbeat", () => undefined);

                source.addEventListener("end", (event) => {
                    if (cancelled) return;

                    setLastEvent({
                        type: "end",
                        data: safeParseEventData(event) || {},
                    });
                    closeEventSource(true);
                });

                source.onerror = () => {
                    if (cancelled) return;

                    setError("Search event connection failed.");
                    setErrorSearchId(searchId);
                    closeEventSource(true);
                };
            } catch (apiError) {
                if (cancelled) return;

                setError(
                    typeof apiError === "string"
                        ? apiError
                        : apiError?.message ||
                              "Failed to connect to search events.",
                );
                setErrorSearchId(searchId);
                setIsConnecting(false);
                setIsConnected(false);
            }
        };

        openSearchEvents();

        return () => {
            cancelled = true;
            closeEventSource(false);
        };
    }, [closeEventSource, dispatch, enabled, eventsUrl, searchId]);

    return {
        status,
        leadCount,
        error,
        errorSearchId,
        isConnected,
        isConnecting,
        lastEvent,
        closeConnection,
    };
};

export default useSearchEvents;
